import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { PlusCircle, Edit, Trash2, Package, Tag, Hash, Box, DollarSign, Warehouse, Calendar, ShoppingCart, Search, ChevronLeft, ChevronRight, Filter, X, ChevronDown, QrCode, Printer } from 'lucide-react';
import InventoryForm from '../components/InventoryForm'; // Importa el nuevo componente
import { productsService } from '../services/productsService'; // Importa el servicio

const InventoryPage = ({ products, setProducts }) => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [showForm, setShowForm] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [loading, setLoading] = useState(false);
  const [localProducts, setLocalProducts] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredProducts, setFilteredProducts] = useState([]);
  // La página se deriva de los searchParams: la URL es la única fuente de verdad
  const page = Number(searchParams.get('page') ?? 1);
  const [itemsPerPage] = useState(30);
  const [filters, setFilters] = useState({
    category: '',
    stockLevel: '',
    priceRange: '',
    location: ''
  });
  const [showFilters, setShowFilters] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [sortConfig, setSortConfig] = useState({
    key: 'name',
    direction: 'asc'
  });
  const [barcodeBuffer, setBarcodeBuffer] = useState('');
  const [lastKeypressTime, setLastKeypressTime] = useState(0);
  const [barcodeNotification, setBarcodeNotification] = useState({ 
    show: false, 
    message: '', 
    type: 'success',
    details: '' 
  });
  // Mapa para búsqueda rápida de productos por código
  const [productSearchMap, setProductSearchMap] = useState({});
  const dropdownRefs = useRef({});

  // Función para cambiar de página y actualizar URL
  const handlePageChange = (newPage) => {
    setSearchParams({ page: String(newPage) });
  };

  // Si la URL no tiene page al montar, restaurarla desde sessionStorage (solo una vez)
  useEffect(() => {
    const pageParam = searchParams.get('page');
    if (!pageParam) {
      const saved = sessionStorage.getItem('inventory_page') || '1';
      setSearchParams({ page: saved });
    }
  }, []);

  // Guardar la página actual en sessionStorage cuando cambie (page es la fuente de verdad)
  useEffect(() => {
    try {
      sessionStorage.setItem('inventory_page', String(page));
    } catch (e) {
      console.warn('No se pudo guardar inventory_page en sessionStorage', e);
    }
  }, [page]);

  // Cargar productos siempre al montar el componente
  useEffect(() => {
    console.log('🚀 InventoryPage useEffect ejecutándose...');
    
    const loadProducts = async () => {
      try {
        setLoading(true);
        console.log('🔄 Iniciando carga de productos...');
        
        // Usar getAllUnpaginated para obtener todos los productos sin límite
        const response = await productsService.getAllUnpaginated();
        console.log('📊 Respuesta completa del servicio:', response);
        console.log('📊 Tipo de respuesta:', typeof response);
        console.log('📊 Es array?:', Array.isArray(response));
        
        // Extraer los datos según el formato de respuesta
        let data = [];
        if (response && response.success && response.data) {
          data = response.data;
          console.log('✅ Usando response.data:', data.length, 'productos');
        } else if (Array.isArray(response)) {
          data = response;
          console.log('✅ Usando response directo (array):', data.length, 'productos');
        } else if (response && response.data && Array.isArray(response.data)) {
          data = response.data;
          console.log('✅ Usando response.data (array):', data.length, 'productos');
        } else {
          console.log('⚠️ Formato de respuesta no reconocido, usando array vacío');
          data = [];
        }
        
        // Validar que cada producto tenga la estructura mínima necesaria
        const validProducts = data.filter(product => {
          if (!product || typeof product !== 'object' || !product.id) {
            console.warn('⚠️ Producto inválido encontrado:', product);
            return false;
          }
          return true;
        });
        
        console.log('✅ Productos válidos:', validProducts.length, 'de', data.length);
        console.log('📋 Primer producto válido:', validProducts[0]);
        
        // Crear un mapa de búsqueda para acceso rápido por códigos
        const searchMap = {};
        validProducts.forEach(product => {
          // Indexar por barcode (código de barras)
          if (product.barcode) {
            const normalizedBarcode = product.barcode.trim().toLowerCase();
            searchMap[normalizedBarcode] = product;
            
            // Versión sin caracteres especiales
            const cleanBarcode = normalizedBarcode.replace(/[^a-z0-9]/g, '');
            if (cleanBarcode && cleanBarcode.length > 3) {
              searchMap[cleanBarcode] = product;
            }
            
            // Versión con guiones en lugar de comillas
            const dashedBarcode = normalizedBarcode.replace(/'/g, '-');
            if (dashedBarcode !== normalizedBarcode) {
              searchMap[dashedBarcode] = product;
            }
          }
          
          // Indexar por SKU
          if (product.sku) {
            const normalizedSku = product.sku.trim().toLowerCase();
            searchMap[normalizedSku] = product;
            
            // Versión sin caracteres especiales
            const cleanSku = normalizedSku.replace(/[^a-z0-9]/g, '');
            if (cleanSku && cleanSku.length > 3) {
              searchMap[cleanSku] = product;
            }
          }
          
          // Indexar por code (código interno)
          if (product.code) {
            const normalizedCode = product.code.trim().toLowerCase();
            searchMap[normalizedCode] = product;
            
            // Versión sin caracteres especiales
            const cleanCode = normalizedCode.replace(/[^a-z0-9]/g, '');
            if (cleanCode && cleanCode.length > 3) {
              searchMap[cleanCode] = product;
            }
          }
        });
        
        console.log('🔍 Mapa de búsqueda creado con', Object.keys(searchMap).length, 'entradas');
        setProductSearchMap(searchMap);
        setLocalProducts(validProducts);
        
        if (setProducts) {
          setProducts(validProducts);
        }
      } catch (error) {
        console.error('❌ Error cargando productos:', error);
        console.error('❌ Stack trace:', error.stack);
        setLocalProducts([]);
        if (setProducts) {
          setProducts([]);
        }
      } finally {
        setLoading(false);
      }
    };

    loadProducts();
  }, []); // Dependencias vacías para cargar siempre

  // Lector de código de barras
  useEffect(() => {
    // Tiempo máximo entre pulsaciones para considerarlas parte del mismo escaneo (ms)
    const MAX_SCAN_GAP = 50;
    // Tiempo máximo para considerar un escaneo completo (ms)
    const SCAN_TIMEOUT = 300;
    // Longitud mínima del código para considerarlo un código de barras válido
    const MIN_BARCODE_LENGTH = 4;
    
    // Función para normalizar códigos de productos específicos
    const normalizeSpecificCodes = (code) => {
      // Lista de patrones conocidos y sus normalizaciones
      const knownPatterns = [
        // Formatos de aceites y lubricantes que pueden contener comillas
        { pattern: /ACE'(\d+)W(\d+)/i, replacement: 'ACE-$1W$2' }, // Ejemplo: ACE'10W40 -> ACE-10W40
        { pattern: /ACE'(\d+[A-Z]\d+)/i, replacement: 'ACE-$1' }, // Más genérico para otros formatos de ACE
        { pattern: /'(\d+W\d+)/i, replacement: '-$1' }, // Ejemplo: '10W40 -> -10W40
        
        // Patrones genéricos para manejar códigos con formato [texto]'[números/letras]
        { pattern: /(\w+)'(\w+)/g, replacement: '$1-$2' }, // Patrón general: TEXTO'TEXTO -> TEXTO-TEXTO
        
  // Reemplazar comillas simples por guiones (caso común en escáneres)
  { pattern: /'([A-Za-z0-9]+)'/g, replacement: '-$1-' }, // Ejemplo: 'ABC123' -> -ABC123-
  { pattern: /'([A-Za-z0-9]+)/g, replacement: '-$1' },   // Ejemplo: 'ABC123 -> -ABC123
  { pattern: /([A-Za-z0-9]+)'/g, replacement: '$1-' },   // Ejemplo: ABC123' -> ABC123-
        
  // Manejar comillas simples sueltas
  { pattern: /'/g, replacement: '-' },  // Reemplazar todas las comillas simples por guiones
  // Manejar comas que algunos escáneres insertan en lugar de guiones
  { pattern: /,/g, replacement: '-' },  // Reemplazar comas por guiones
        
        // Limpiar guiones múltiples consecutivos que pudieran generarse
        { pattern: /-{2,}/g, replacement: '-' }, // Ejemplo: --- -> -
        
        // Quitar espacios excesivos que pudieran confundir la búsqueda
        { pattern: /\s+/g, replacement: ' ' },
      ];
      
      // Aplicar cada patrón de normalización
      let normalizedCode = code;
      knownPatterns.forEach(({ pattern, replacement }) => {
        normalizedCode = normalizedCode.replace(pattern, replacement);
      });
      
      return normalizedCode;
    };
    
    // Función para manejar las pulsaciones de teclas
    const handleKeyDown = (event) => {
      // Ignorar si el usuario está escribiendo en un campo de entrada (input, textarea, select, etc.)
      const activeElement = document.activeElement;
      const isInputField = activeElement && (
        activeElement.tagName === 'INPUT' ||
        activeElement.tagName === 'TEXTAREA' ||
        activeElement.tagName === 'SELECT' ||
        activeElement.isContentEditable
      );
      
      // Si el usuario está en un campo de entrada, no procesar como código de barras
      if (isInputField) return;
      
      // Ignorar eventos de teclas modificadoras, navegación y teclas especiales
      if (event.ctrlKey || event.altKey || event.metaKey) return;
      if (event.key === 'Shift' || event.key === 'Control' || event.key === 'Alt' || event.key === 'Meta') return;
      if (event.key === 'Tab' || event.key === 'Escape' || event.key === 'ArrowUp' || 
          event.key === 'ArrowDown' || event.key === 'ArrowLeft' || event.key === 'ArrowRight') return;
          
      // Ignorar la tecla Backspace y otras teclas de control para evitar procesarlas como códigos de barras
      if (event.key === 'Backspace' || event.key === 'Delete' || event.key === 'Home' || 
          event.key === 'End' || event.key === 'PageUp' || event.key === 'PageDown' ||
          event.key === 'F1' || event.key === 'F2' || event.key === 'F3' || event.key === 'F4' ||
          event.key === 'F5' || event.key === 'F6' || event.key === 'F7' || event.key === 'F8' ||
          event.key === 'F9' || event.key === 'F10' || event.key === 'F11' || event.key === 'F12') {
        // Solo permitir que Backspace borre caracteres del buffer si hay contenido
        if (event.key === 'Backspace' && barcodeBuffer.length > 0) {
          setBarcodeBuffer(prevBuffer => prevBuffer.slice(0, -1));
          setLastKeypressTime(new Date().getTime());
        }
        return;
      }
      
      const currentTime = new Date().getTime();
      const timeSinceLastKeypress = currentTime - lastKeypressTime;
      
      // Los escáneres de código de barras generalmente envían caracteres muy rápidamente
      if (timeSinceLastKeypress > MAX_SCAN_GAP && barcodeBuffer.length > 0) {
        // Si ha pasado más tiempo, es una nueva lectura o interacción manual
        // Solo resetear si ya teníamos algo en el buffer (podría ser una entrada manual lenta)
        if (timeSinceLastKeypress > SCAN_TIMEOUT) {
          setBarcodeBuffer('');
        }
      }
      
      // Procesar el caracter
      if (event.key === 'Enter') {
        // El Enter típicamente señala el final de un escaneo de código de barras
        event.preventDefault(); // Prevenir la acción predeterminada para no enviar formularios
        
        if (barcodeBuffer.length >= MIN_BARCODE_LENGTH) {
          processBarcodeInput();
        } else if (barcodeBuffer.length > 0) {
          console.log('⚠️ Código demasiado corto, ignorando:', barcodeBuffer);
          setBarcodeBuffer('');
          
          // Mostrar notificación de código inválido
          setBarcodeNotification({
            show: true,
            message: 'Código demasiado corto para ser válido',
            type: 'warning'
          });
          
          setTimeout(() => {
            setBarcodeNotification(prev => ({ ...prev, show: false }));
          }, 2000);
        }
      } else {
        // Acumular el caracter en el buffer usando try-catch para manejar caracteres problemáticos
        try {
          setBarcodeBuffer(prevBuffer => prevBuffer + event.key);
        } catch (error) {
          console.error('Error al añadir caracter al buffer:', error);
          // Si hay un error, intentamos añadir un valor seguro
          setBarcodeBuffer(prevBuffer => prevBuffer + '_');
        }
      }
      
      setLastKeypressTime(currentTime);
    };

    // Función para procesar el código de barras completo de manera optimizada
    const processBarcodeInput = () => {
      // Lista de teclas especiales que no deberían considerarse como códigos de barras válidos
      const specialKeys = ['Backspace', 'Delete', 'Enter', 'Tab', 'Escape', 'Home', 'End', 
                          'PageUp', 'PageDown', 'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight',
                          'F1', 'F2', 'F3', 'F4', 'F5', 'F6', 'F7', 'F8', 'F9', 'F10', 'F11', 'F12'];
      
      // Verificar si el buffer contiene solo una tecla especial (rápida verificación)
      if (specialKeys.includes(barcodeBuffer)) {
        console.log('🔄 Ignorando tecla especial:', barcodeBuffer);
        setBarcodeBuffer('');
        return;
      }
      
      if (barcodeBuffer.length >= MIN_BARCODE_LENGTH) {
        // Tiempo de inicio para medir rendimiento
        const startTime = performance.now();
        
        // Limpiamos el código de barras para evitar caracteres problemáticos
        const sanitizedBarcode = barcodeBuffer.replace(/['"\\]/g, ''); // Eliminar comillas y barras invertidas
        
        // Aplicar normalización específica para formatos conocidos
        const normalizedBarcode = normalizeSpecificCodes(barcodeBuffer);
        
        console.log('🔍 Código de barras escaneado (original):', barcodeBuffer);
        
        // Buscar primero en el mapa de búsqueda rápida (esto es más rápido)
        let foundProduct = null;
        const searchKeys = [
          barcodeBuffer.trim().toLowerCase(),
          sanitizedBarcode.trim().toLowerCase(),
          normalizedBarcode.trim().toLowerCase(),
          barcodeBuffer.replace(/'/g, '-').trim().toLowerCase(),
          barcodeBuffer.replace(/,/g, '-').trim().toLowerCase(),
          barcodeBuffer.replace(/[^a-zA-Z0-9]/g, '').trim().toLowerCase(),
          sanitizedBarcode.replace(/[^a-zA-Z0-9]/g, '').trim().toLowerCase(),
          normalizedBarcode.replace(/[^a-zA-Z0-9]/g, '').trim().toLowerCase()
        ];
        
        // Intentar encontrar el producto en el mapa de búsqueda (más rápido)
        for (const key of searchKeys) {
          if (productSearchMap[key]) {
            foundProduct = productSearchMap[key];
            console.log('✅ Producto encontrado rápidamente por clave:', key);
            break;
          }
        }
        
        // Si no se encontró en el mapa, intentar el método tradicional
        if (!foundProduct) {
          // Crear un array de posibles formatos para el método tradicional
          const possibleCodes = [
            sanitizedBarcode.trim(),
            normalizedBarcode.trim(),
            barcodeBuffer.trim(),
            barcodeBuffer.replace(/'/g, '-').trim(),
            barcodeBuffer.replace(/[^a-zA-Z0-9]/g, '').trim(),
            sanitizedBarcode.replace(/[^a-zA-Z0-9]/g, '').trim(),
            normalizedBarcode.replace(/[^a-zA-Z0-9]/g, '').trim()
          ];
          
          foundProduct = (products && products.length > 0 ? products : localProducts).find(
            product => {
              // Obtener los códigos del producto y normalizarlos
              const productBarcode = product.barcode ? product.barcode.trim() : '';
              const productSku = product.sku ? product.sku.trim() : '';
              const productCode = product.code ? product.code.trim() : '';
              
              // También normalizar los códigos del producto para mayor compatibilidad
              const normalizedBarcode = productBarcode ? normalizeSpecificCodes(productBarcode) : '';
              const normalizedSku = productSku ? normalizeSpecificCodes(productSku) : '';
              const normalizedCode = productCode ? normalizeSpecificCodes(productCode) : '';
              
              // Crear un array con todos los posibles códigos del producto
              const productCodes = [
                productBarcode, productSku, productCode,
                normalizedBarcode, normalizedSku, normalizedCode,
                // Versiones en minúsculas
                productBarcode.toLowerCase(), productSku.toLowerCase(), productCode.toLowerCase(),
                normalizedBarcode.toLowerCase(), normalizedSku.toLowerCase(), normalizedCode.toLowerCase(),
                // Versiones sin caracteres especiales
                productBarcode.replace(/[^a-zA-Z0-9]/g, ''),
                productSku.replace(/[^a-zA-Z0-9]/g, ''),
                productCode.replace(/[^a-zA-Z0-9]/g, '')
              ].filter(code => code !== ''); // Eliminar valores vacíos
            
            // Verificar coincidencias entre los códigos del producto y los posibles formatos del código escaneado
            return possibleCodes.some(scannedCode => {
              if (!scannedCode) return false;
              
              // Comprobar coincidencia exacta o con normalización
              return productCodes.some(productCode => {
                if (!productCode) return false;
                
                // Comprobar coincidencia exacta
                if (productCode === scannedCode) return true;
                
                // Comprobar si el código escaneado está contenido en el código del producto (para casos parciales)
                if (productCode.includes(scannedCode) && scannedCode.length >= 5) return true;
                
                // Comprobar si el código del producto está contenido en el código escaneado (para casos con prefijos/sufijos)
                if (scannedCode.includes(productCode) && productCode.length >= 5) return true;
                
                return false;
              });
            });
          }
        );
        }
        
        // Medir el tiempo que tomó la búsqueda
        const endTime = performance.now();
        const searchTime = (endTime - startTime).toFixed(2);
        console.log(`🕒 Búsqueda completada en ${searchTime}ms`);
        
        if (foundProduct) {
          console.log('✅ Producto encontrado:', foundProduct);
          
          // Navegar inmediatamente a la ficha del producto
          navigate(`/inventory/product/${foundProduct.id}?page=${page}`);
          
          // Mostrar notificación de éxito (aparecerá brevemente antes de la navegación)
          setBarcodeNotification({
            show: true,
            message: `Producto encontrado: ${foundProduct.name}`,
            type: 'success',
            details: `SKU: ${foundProduct.sku || ''} | Código: ${foundProduct.code || ''}`
          });
          
          // Limpiar la notificación después de un momento por si el usuario vuelve atrás
          setTimeout(() => {
            setBarcodeNotification({ show: false, message: '', type: 'success', details: '' });
          }, 500);
        } else {
          // Construir mensaje con información útil para depuración
          const searchDetails = `Original: "${barcodeBuffer}", 
                                 Sanitizado: "${sanitizedBarcode}", 
                                 Normalizado: "${normalizedBarcode}"`;
          
          console.log('❌ No se encontró ningún producto:', searchDetails);
          
          // Mostrar notificación de error con información útil
          setBarcodeNotification({
            show: true,
            message: `No se encontró producto con código: ${normalizedBarcode}`,
            type: 'error',
            details: `Prueba buscar manualmente con: ${barcodeBuffer}`
          });
          
          // Ocultar la notificación después de un tiempo
          setTimeout(() => {
            setBarcodeNotification({ show: false, message: '', type: 'error', details: '' });
          }, 3000);
        }
        
        // Limpiar el buffer para la siguiente lectura
        setBarcodeBuffer('');
      }
    };

    // Configurar y limpiar el event listener
    document.addEventListener('keydown', handleKeyDown);
    
    // Configurar un temporizador para procesar la entrada si no se recibe un Enter
    const timer = setTimeout(() => {
      if (barcodeBuffer.length >= MIN_BARCODE_LENGTH) {
        processBarcodeInput();
      }
    }, SCAN_TIMEOUT);
    
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      clearTimeout(timer);
    };
  }, [barcodeBuffer, lastKeypressTime, products, localProducts, navigate, productSearchMap]);

  // Cerrar dropdowns al hacer click fuera
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (activeDropdown && dropdownRefs.current[activeDropdown] && 
          !dropdownRefs.current[activeDropdown].contains(event.target)) {
        setActiveDropdown(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [activeDropdown]);

  // Filtrar productos basado en el término de búsqueda y filtros
  useEffect(() => {
    const productsToFilter = products && products.length > 0 ? products : localProducts;
    
    let filtered = productsToFilter;

    // Validar que tenemos productos válidos
    if (!filtered || !Array.isArray(filtered)) {
      filtered = [];
    } else {
      // Filtrar productos válidos (que no sean null o undefined)
      filtered = filtered.filter(product => product && typeof product === 'object' && product.id);
    }

    // Filtro de búsqueda general
    if (searchTerm.trim()) {
      filtered = filtered.filter(product =>
        product.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        product.sku?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        product.code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        product.category?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        product.all_skus?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        product.description?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Filtro por categoría
    if (filters.category) {
      filtered = filtered.filter(product => 
        product.category && product.category.toLowerCase().includes(filters.category.toLowerCase())
      );
    }

    // Filtro por nivel de stock
    if (filters.stockLevel) {
      filtered = filtered.filter(product => {
        const stock = product.current_stock || product.stock || product.quantity || 0;
        const minStock = product.min_stock || product.minimum_stock || 5;
        const maxStock = product.max_stock || product.maximum_stock || 50;
        const status = getStockStatus(stock, minStock, maxStock);
        
        switch (filters.stockLevel) {
          case 'agotado':
            return stock === 0;
          case 'critico':
            return status.color === 'red' && stock > 0;
          case 'bajo':
            return status.color === 'yellow';
          case 'normal':
            return status.color === 'green';
          default:
            return true;
        }
      });
    }

    // Filtro por rango de precio
    if (filters.priceRange) {
      filtered = filtered.filter(product => {
        const price = parseFloat(product.unit_price) || 0;
        switch (filters.priceRange) {
          case 'bajo':
            return price < 100;
          case 'medio':
            return price >= 100 && price < 500;
          case 'alto':
            return price >= 500;
          default:
            return true;
        }
      });
    }

    // Filtro por ubicación
    if (filters.location) {
      filtered = filtered.filter(product => 
        product.location && product.location.toLowerCase().includes(filters.location.toLowerCase())
      );
    }

    // Aplicar ordenamiento
    if (sortConfig.key) {
      filtered.sort((a, b) => {
        let aValue = a[sortConfig.key];
        let bValue = b[sortConfig.key];

        // Manejar valores nulos/undefined
        if (aValue == null) aValue = '';
        if (bValue == null) bValue = '';

        // Convertir a string para comparación
        if (typeof aValue === 'string') {
          aValue = aValue.toLowerCase();
        }
        if (typeof bValue === 'string') {
          bValue = bValue.toLowerCase();
        }

        // Para números
        if (sortConfig.key === 'unit_price' || sortConfig.key === 'stock' || sortConfig.key === 'current_stock') {
          aValue = parseFloat(aValue) || 0;
          bValue = parseFloat(bValue) || 0;
        }

        if (aValue < bValue) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }

    setFilteredProducts(filtered);
  }, [products, localProducts, searchTerm, filters, sortConfig]);

  // Calcular productos para la página actual
  const totalProducts = filteredProducts.length;
  const totalPages = Math.ceil(totalProducts / itemsPerPage);
  const startIndex = (page - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentProducts = filteredProducts.slice(startIndex, endIndex);

  // Usar productos de la página actual
  const productsToShow = currentProducts;

  // Debug logs
  console.log('🔍 InventoryPage - Estado actual:', {
    products: products?.length || 0,
    localProducts: localProducts?.length || 0,
    filteredProducts: filteredProducts?.length || 0,
    productsToShow: productsToShow?.length || 0,
    page,
    totalPages,
    loading
  });

  const handleAddProduct = () => {
    setEditingProduct(null);
    setShowForm(true);
  };

  const handleEditProduct = (product) => {
    setEditingProduct(product);
    setShowForm(true);
  };

  const handleDeleteProduct = async (id) => {
    if (window.confirm('¿Estás seguro de que quieres eliminar este producto? ¡Podrías detener un camión!')) {
      try {
        setLoading(true);
        await productsService.delete(id);
        
        // Actualizar tanto props como estado local
        const updatedProducts = filteredProducts.filter(p => p.id !== id);
        if (setProducts) {
          setProducts(updatedProducts);
        }
        setLocalProducts(updatedProducts);
        
        console.log('✅ Producto eliminado exitosamente');
      } catch (error) {
        console.error('❌ Error eliminando producto:', error);
        alert('Error eliminando el producto: ' + error.message);
      } finally {
        setLoading(false);
      }
    }
  };

  const handleSaveProduct = async (productData) => {
    try {
      setLoading(true);
      let result;
      
      if (productData.id) {
        // Editar producto existente
        result = await productsService.update(productData.id, productData);
        console.log('✅ Resultado actualización:', result);
        
        // Extraer el producto actualizado de la respuesta
        const updatedProduct = result.success && result.data ? result.data : result;
        
        // Actualizar tanto props como estado local
        const updatedProducts = localProducts.map(p => 
          p.id === productData.id ? updatedProduct : p
        );
        if (setProducts) {
          setProducts(updatedProducts);
        }
        setLocalProducts(updatedProducts);
        
        console.log('✅ Producto actualizado exitosamente');
      } else {
        // Añadir nuevo producto
        result = await productsService.create(productData);
        console.log('✅ Resultado creación:', result);
        
        // Extraer el nuevo producto de la respuesta
        const newProduct = result.success && result.data ? result.data : result;
        
        // Agregar a ambos estados
        const updatedProducts = [...localProducts, newProduct];
        if (setProducts) {
          setProducts(updatedProducts);
        }
        setLocalProducts(updatedProducts);
        
        console.log('✅ Producto creado exitosamente');
      }
      
      setShowForm(false);
      setEditingProduct(null);
    } catch (error) {
      console.error('❌ Error guardando producto:', error);
      alert('Error guardando el producto: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCancelForm = () => {
    setShowForm(false);
    setEditingProduct(null);
  };

  const handleFilterChange = (filterKey, value) => {
    setFilters(prev => ({
      ...prev,
      [filterKey]: value
    }));
    // Al aplicar un filtro, ir a la primera página (URL es la fuente de verdad)
    setSearchParams({ page: '1' });
  };

  const clearFilters = () => {
    setFilters({
      category: '',
      stockLevel: '',
      priceRange: '',
      location: ''
    });
    setSearchTerm('');
    setSearchParams({ page: '1' });
  };

  const handlePrintInventory = () => {
    const dataToPrint = Array.isArray(filteredProducts) && filteredProducts.length > 0
      ? filteredProducts
      : (Array.isArray(localProducts) ? localProducts : []);

    const date = new Date();
    const dateStr = date.toLocaleString('es-MX');
    const currency = (v) => {
      const num = parseFloat(v) || 0;
      try {
        return new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(num);
      } catch {
        return `$${num.toFixed(2)}`;
      }
    };

    const htmlRows = dataToPrint
      .filter(p => p && typeof p === 'object' && p.id)
      .map((p, idx) => {
        const stock = p.current_stock || p.stock || p.quantity || 0;
        const min = p.min_stock || p.minimum_stock || 0;
        const max = p.max_stock || p.maximum_stock || 0;
        return `
          <tr>
            <td>${idx + 1}</td>
            <td>${p.code || ''}</td>
            <td>${(p.name || '').toString().replace(/</g,'&lt;')}</td>
            <td>${p.sku || ''}</td>
            <td>${p.category || ''}</td>
            <td style="text-align:right;">${stock}</td>
            <td style="text-align:right;">${min}</td>
            <td style="text-align:right;">${max}</td>
            <td>${p.location || ''}</td>
          </tr>`;
      }).join('');

    const totalCount = dataToPrint.filter(p => p && typeof p === 'object' && p.id).length;

    const html = `<!DOCTYPE html>
    <html lang="es">
      <head>
        <meta charset="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <title>Inventario - ${dateStr}</title>
        <style>
          body { font-family: Arial, Helvetica, sans-serif; padding: 24px; color: #111827; }
          h1 { margin: 0 0 4px 0; font-size: 22px; }
          .subtitle { color: #6b7280; margin-bottom: 16px; }
          table { width: 100%; border-collapse: collapse; font-size: 12px; }
          th, td { border: 1px solid #e5e7eb; padding: 8px; }
          th { background: #f3f4f6; text-align: left; }
          tfoot td { font-weight: bold; background: #f9fafb; }
          /* Reglas de impresión para encabezado/pie de tabla */
          thead { display: table-header-group; }
          tfoot { display: table-footer-group; }
          tr, td, th { page-break-inside: avoid; }
          @page { margin: 16mm; }
          @media print { 
            .no-print { display: none; } 
            body { padding: 0; }
            .content-wrapper { padding: 0; }
          }
        </style>
      </head>
      <body>
        <div class=\"content-wrapper\">
        <div style=\"display:flex; justify-content: space-between; align-items:center; margin-bottom: 12px;\">
          <div>
            <h1>Sistema de Almacén — Inventario</h1>
            <div class="subtitle">Fecha de impresión: ${dateStr}</div>
          </div>
          <button class=\"no-print\" onclick=\"window.print()\" style=\"padding:8px 12px; border:1px solid #d1d5db; border-radius:8px; background:#fff; cursor:pointer;\">Imprimir</button>
        </div>
        <table>
          <thead>
            <tr>
              <th>#</th>
              <th>Código</th>
              <th>Producto</th>
              <th>SKU</th>
              <th>Categoría</th>
              <th style="text-align:right;">Stock</th>
              <th style="text-align:right;">Mín.</th>
              <th style="text-align:right;">Máx.</th>
              <th>Ubicación</th>
            </tr>
          </thead>
          <tbody>
            ${htmlRows}
          </tbody>
          <tfoot>
            <tr>
              <td colspan=\"9\" style=\"font-size:11px; color:#374151;\">reporte elaborado automaticamente por el SistemaDeAlmacenTITSA programado por el Ing. José Antonio Leal De La Garza</td>
            </tr>
          </tfoot>
        </table>
        <div style=\"margin-top:8px; font-weight:bold;\">Total de productos listados: ${totalCount}</div>
        </div>
      </body>
    </html>`;
    // Imprimir usando iframe oculto (no abre nueva pestaña)
    try {
      const iframe = document.createElement('iframe');
      iframe.style.position = 'fixed';
      iframe.style.right = '0';
      iframe.style.bottom = '0';
      iframe.style.width = '0';
      iframe.style.height = '0';
      iframe.style.border = '0';
      document.body.appendChild(iframe);
      const doc = iframe.contentWindow?.document;
      if (!doc) throw new Error('No se pudo acceder al documento del iframe');
      doc.open();
      doc.write(html);
      doc.close();
      setTimeout(() => {
        try {
          iframe.contentWindow?.focus();
          iframe.contentWindow?.print();
        } catch (e) {
          console.error('Error imprimiendo desde iframe:', e);
          alert('No se pudo iniciar la impresión. Revisa el bloqueador de ventanas emergentes.');
        }
        // Limpiar el iframe después de intentar imprimir
        setTimeout(() => {
          try { document.body.removeChild(iframe); } catch {}
        }, 2000);
      }, 200);
    } catch (e) {
      console.error('Impresión con iframe falló:', e);
      alert('No se pudo abrir la vista de impresión. Intenta nuevamente.');
    }
  };

  const getActiveFiltersCount = () => {
    return Object.values(filters).filter(value => value !== '').length + (searchTerm ? 1 : 0);
  };

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const getSortIcon = (columnKey) => {
    if (sortConfig.key !== columnKey) {
      return '↕️';
    }
    return sortConfig.direction === 'asc' ? '↑' : '↓';
  };

  // Obtener valores únicos para los filtros
  const getUniqueCategories = () => {
    const allProducts = products && products.length > 0 ? products : localProducts;
    if (!allProducts || !Array.isArray(allProducts)) return [];
    return [...new Set(allProducts.filter(p => p && typeof p === 'object').map(p => p.category).filter(Boolean))];
  };

  const getUniqueLocations = () => {
    const allProducts = products && products.length > 0 ? products : localProducts;
    if (!allProducts || !Array.isArray(allProducts)) return [];
    return [...new Set(allProducts.filter(p => p && typeof p === 'object').map(p => p.location).filter(Boolean))];
  };

  const toggleDropdown = (columnName) => {
    setActiveDropdown(activeDropdown === columnName ? null : columnName);
  };

  // Componente para filtro de columna estilo Excel
  const ColumnFilter = ({ columnName, title, filterKey, options, currentValue, sortKey }) => {
    return (
      <div className="relative" ref={el => dropdownRefs.current[columnName] = el}>
        <button
          onClick={() => toggleDropdown(columnName)}
          className="flex items-center justify-between w-full text-center text-xs font-medium text-gray-500 uppercase tracking-wider hover:text-gray-700 transition-colors"
        >
          <span className="flex items-center gap-1">
            {title}
            {sortKey && (
              <span className="text-blue-600 font-bold">
                {getSortIcon(sortKey)}
              </span>
            )}
          </span>
          <ChevronDown 
            className={`w-3 h-3 ml-1 transition-transform ${
              activeDropdown === columnName ? 'rotate-180' : ''
            } ${currentValue ? 'text-blue-600' : ''}`}
          />
        </button>
        
        <AnimatePresence>
          {activeDropdown === columnName && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-50 min-w-[220px]"
            >
              <div className="p-2 max-h-60 overflow-y-auto">
                {/* Opciones de ordenamiento */}
                {sortKey && (
                  <>
                    <div className="px-3 py-2 text-xs font-semibold text-gray-600 uppercase tracking-wider border-b border-gray-100">
                      📊 Ordenar
                    </div>
                    <button
                      onClick={() => {
                        handleSort(sortKey);
                        setActiveDropdown(null);
                      }}
                      className="w-full text-left px-3 py-2 text-sm hover:bg-gray-100 rounded flex items-center gap-2"
                    >
                      <span className="text-blue-600">↑</span>
                      A → Z (Ascendente)
                    </button>
                    <button
                      onClick={() => {
                        setSortConfig({ key: sortKey, direction: 'desc' });
                        setActiveDropdown(null);
                      }}
                      className="w-full text-left px-3 py-2 text-sm hover:bg-gray-100 rounded flex items-center gap-2"
                    >
                      <span className="text-blue-600">↓</span>
                      Z → A (Descendente)
                    </button>
                    <div className="border-t border-gray-100 my-1"></div>
                  </>
                )}

                {/* Opciones de filtrado */}
                <div className="px-3 py-2 text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  🔍 Filtrar
                </div>
                <button
                  onClick={() => {
                    handleFilterChange(filterKey, '');
                    setActiveDropdown(null);
                  }}
                  className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-100 rounded ${
                    !currentValue ? 'bg-blue-50 text-blue-700 font-medium' : 'text-gray-700'
                  }`}
                >
                  {filterKey === 'stockLevel' ? '📋 Todos los niveles' : 
                   filterKey === 'priceRange' ? '💰 Todos los precios' :
                   filterKey === 'category' ? '🏷️ Todas las categorías' :
                   '📍 Todas las ubicaciones'}
                </button>
                
                <div className="border-t border-gray-100 my-1"></div>
                
                {options.map((option) => (
                  <button
                    key={option.value}
                    onClick={() => {
                      handleFilterChange(filterKey, option.value);
                      setActiveDropdown(null);
                    }}
                    className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-100 rounded ${
                      currentValue === option.value ? 'bg-blue-50 text-blue-700 font-medium' : 'text-gray-700'
                    }`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    );
  };

  const getQuantityColorClass = (quantity) => {
    if (quantity === 0) {
      return 'text-red-700 font-bold';
    } else if (quantity >= 1 && quantity <= 5) {
      return 'text-red-600 font-semibold';
    } else if (quantity >= 6 && quantity <= 9) {
      return 'text-yellow-600 font-semibold';
    } else {
      return 'text-green-600 font-semibold';
    }
  };

  const getRowColorClass = (current, min, max) => {
    const status = getStockStatus(current, min, max);
    
    switch (status.color) {
      case 'red':
        return current === 0 
          ? 'bg-red-100 hover:bg-red-150 border-l-4 border-red-500'
          : 'bg-red-50 hover:bg-red-100 border-l-4 border-red-400';
      case 'yellow':
        return 'bg-yellow-50 hover:bg-yellow-100 border-l-4 border-yellow-400';
      case 'green':
        return 'bg-green-50 hover:bg-green-100 border-l-4 border-green-400';
      default:
        return 'hover:bg-blue-50 border-l-4 border-transparent';
    }
  };

  const getStockStatus = (current, min, max) => {
    const currentStock = current || 0;
    const minStock = min || 0;
    const maxStock = max || 100;

    if (currentStock === 0) {
      return {
        color: 'red',
        label: 'AGOTADO',
        percentage: 0,
        bgColor: 'bg-red-500',
        textColor: 'text-red-700',
        icon: '🔴'
      };
    } else if (currentStock <= minStock) {
      // Solo es crítico si está en o por debajo del mínimo
      return {
        color: 'red',
        label: 'CRÍTICO',
        percentage: Math.min((currentStock / maxStock) * 100, 100),
        bgColor: 'bg-red-500',
        textColor: 'text-red-600',
        icon: '🟠'
      };
    } else if (currentStock < maxStock * 0.7) {
      // Bajo si está entre mínimo y 70% del máximo
      return {
        color: 'yellow',
        label: 'BAJO',
        percentage: Math.min((currentStock / maxStock) * 100, 100),
        bgColor: 'bg-yellow-500',
        textColor: 'text-yellow-600',
        icon: '🟡'
      };
    } else {
      // Normal si está en 70% o más del máximo
      return {
        color: 'green',
        label: 'NORMAL',
        percentage: Math.min((currentStock / maxStock) * 100, 100),
        bgColor: 'bg-green-500',
        textColor: 'text-green-600',
        icon: '🟢'
      };
    }
  };

  // Componente de barra de stock visual
  const StockProgressBar = ({ current, min, max, compact = true }) => {
    const status = getStockStatus(current, min, max);
    const currentStock = current || 0;
    const minStock = min || 0;
    const maxStock = max || 100;

    return (
      <div className={`${compact ? 'w-full' : 'w-full'}`}>
        {/* Información del stock */}
        <div className="flex items-center justify-between mb-1">
          <div className={`flex items-center gap-1 text-sm font-semibold ${status.textColor}`}>
            <span className="text-lg">{status.icon}</span>
            <Box className="w-4 h-4" />
            <span>{currentStock} {compact ? '' : 'pcs'}</span>
          </div>
          {!compact && (
            <span className={`text-xs px-2 py-0.5 rounded-full font-medium bg-${status.color}-100 text-${status.color}-700`}>
              {status.label}
            </span>
          )}
        </div>

        {/* Barra de progreso */}
        <div className="relative w-full bg-gray-200 rounded-full h-2 overflow-hidden">
          {/* Barra de progreso principal */}
          <div
            className={`h-full transition-all duration-500 ease-out ${status.bgColor}`}
            style={{ width: `${Math.min(status.percentage, 100)}%` }}
          />
          
          {/* Marcadores de min y max */}
          {maxStock > 0 && (
            <>
              {/* Marcador de stock mínimo */}
              {minStock > 0 && (
                <div
                  className="absolute top-0 w-0.5 h-full bg-orange-400 shadow-sm"
                  style={{ left: `${Math.min((minStock / maxStock) * 100, 100)}%` }}
                  title={`Stock mínimo: ${minStock}`}
                />
              )}
              
              {/* Marcador de stock máximo */}
              <div
                className="absolute top-0 w-0.5 h-full bg-blue-400 shadow-sm"
                style={{ left: '100%', transform: 'translateX(-1px)' }}
                title={`Stock máximo: ${maxStock}`}
              />
            </>
          )}
        </div>

        {/* Indicadores compactos para la tabla */}
        {compact && (
          <div className="flex items-center justify-between mt-1 text-xs text-gray-500">
            <span>Min: {minStock}</span>
            <span className={`px-2 py-0.5 rounded-full text-xs font-medium bg-${status.color}-100 text-${status.color}-700`}>
              {status.label}
            </span>
            <span>Max: {maxStock}</span>
          </div>
        )}
      </div>
    );
  };

  return (
    <motion.div
      className="min-h-[calc(100vh-180px)] py-8 px-12"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
    >
      <AnimatePresence mode="wait">
        {showForm && (
          <InventoryForm
            currentProduct={editingProduct}
            onSave={handleSaveProduct}
            onCancel={handleCancelForm}
          />
        )}
      </AnimatePresence>

      {!showForm && (
        <>
          <h2 className="text-4xl font-extrabold text-gray-800 mb-8 text-center bg-gradient-to-r from-green-600 to-teal-600 bg-clip-text text-transparent">
            Inventario de Refacciones
          </h2>

          {/* Barra de búsqueda */}
          <div className="flex justify-center mb-6">
            <div className="relative w-full max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Buscar por nombre, SKU, código o categoría..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setSearchParams({ page: '1' });
                }}
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent outline-none transition-all duration-300 text-gray-700 placeholder-gray-400"
              />
              {barcodeBuffer.length > 0 && (
                <div className="absolute right-3 top-1/2 transform -translate-y-1/2 bg-blue-100 text-blue-800 text-xs font-medium px-2.5 py-0.5 rounded flex items-center">
                  <QrCode className="w-4 h-4 mr-1" />
                  Escaneando: {barcodeBuffer}
                </div>
              )}
            </div>
          </div>

          {/* Contador de filtros activos */}
          {getActiveFiltersCount() > 0 && (
            <div className="flex justify-center mb-4">
              <motion.button
                onClick={clearFilters}
                className="flex items-center gap-2 px-4 py-2 text-sm bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <Filter className="w-4 h-4" />
                {getActiveFiltersCount()} filtro{getActiveFiltersCount() > 1 ? 's' : ''} activo{getActiveFiltersCount() > 1 ? 's' : ''}
                <X className="w-4 h-4 ml-1" />
                Limpiar
              </motion.button>
            </div>
          )}

          <div className="flex justify-center gap-4 mb-8">
            <motion.button
              onClick={handleAddProduct}
              className="flex items-center gap-2 px-6 py-3 rounded-xl font-semibold text-lg bg-gradient-to-r from-blue-500 to-purple-600 text-white shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.98 }}
            >
              <PlusCircle className="w-5 h-5" />
              Nuevo Producto
            </motion.button>
            
            <motion.button
              onClick={() => navigate('/chemical-products')}
              className="flex items-center gap-2 px-6 py-3 rounded-xl font-semibold text-lg bg-gradient-to-r from-yellow-500 to-orange-600 text-white shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.98 }}
            >
              <Package className="w-5 h-5" />
              Productos Químicos
            </motion.button>
            
            <motion.button
              onClick={handlePrintInventory}
              className="flex items-center gap-2 px-6 py-3 rounded-xl font-semibold text-lg bg-gradient-to-r from-green-600 to-emerald-600 text-white shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.98 }}
              title="Imprimir todo el inventario (aplica filtros actuales)"
            >
              <Printer className="w-5 h-5" />
              Imprimir inventario
            </motion.button>
            
            <motion.div
              className="flex items-center gap-2 px-6 py-3 rounded-xl font-semibold text-md bg-gradient-to-r from-gray-100 to-gray-200 text-gray-700 shadow-sm border border-gray-300"
              whileHover={{ y: -2 }}
              transition={{ duration: 0.2 }}
            >
              <QrCode className="w-5 h-5 text-blue-600" />
              Utiliza un escáner de código de barras para encontrar productos
            </motion.div>
          </div>

          <motion.div
          className="bg-white shadow-2xl w-full border-y border-gray-200"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3, duration: 0.5 }}
        >
            {/* Notificación de código de barras */}
            <AnimatePresence>
              {barcodeNotification.show && (
                <motion.div
                  initial={{ opacity: 0, y: -20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  className={`fixed top-4 right-4 z-50 p-4 rounded-lg shadow-lg flex items-start max-w-md ${
                    barcodeNotification.type === 'success' 
                      ? 'bg-green-100 text-green-800 border-l-4 border-green-500' 
                      : barcodeNotification.type === 'warning'
                        ? 'bg-yellow-100 text-yellow-800 border-l-4 border-yellow-500'
                        : 'bg-red-100 text-red-800 border-l-4 border-red-500'
                  }`}
                >
                  <QrCode className="w-5 h-5 mr-2" />
                  <div>
                    <p className="font-medium">{barcodeNotification.message}</p>
                    {barcodeNotification.details && (
                      <p className="text-sm mt-1 opacity-80">{barcodeNotification.details}</p>
                    )}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
            
            {loading && (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                <p className="text-blue-700 font-medium">Cargando productos...</p>
              </div>
            )}
          
          {!loading && productsToShow.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <Package className="w-20 h-20 mx-auto mb-4 text-gray-300" />
              <p className="text-xl font-medium">No hay productos en el inventario. ¡Añade refacciones!</p>
            </div>
          ) : (
            <div className="w-full">
              <div className="mb-4 text-center">
                <h3 className="text-2xl font-bold text-gray-800">
                  📦 Productos en Inventario ({totalProducts})
                </h3>
                <p className="text-sm text-gray-600 mt-1">
                      Página {page} de {totalPages} • Mostrando {startIndex + 1}-{Math.min(endIndex, totalProducts)} de {totalProducts} productos
                </p>
              </div>
              <div className="overflow-x-auto shadow-lg rounded-lg">
                <table className="w-full table-auto divide-y divide-gray-200 bg-white">
                  <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-center">
                      <button
                        onClick={() => handleSort('code')}
                        className="flex items-center justify-center w-full hover:text-gray-700 transition-colors"
                      >
                        <Hash className="w-4 h-4 text-gray-500 mr-2" />
                        <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Código {getSortIcon('code')}
                        </span>
                      </button>
                    </th>
                    <th scope="col" className="px-6 py-3 text-center">
                      <button
                        onClick={() => handleSort('name')}
                        className="flex items-center justify-center w-full hover:text-gray-700 transition-colors"
                      >
                        <Tag className="w-4 h-4 text-gray-500 mr-2" />
                        <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Producto {getSortIcon('name')}
                        </span>
                      </button>
                    </th>
                    <th scope="col" className="px-6 py-3 text-center">
                      <button
                        onClick={() => handleSort('sku')}
                        className="flex items-center justify-center w-full hover:text-gray-700 transition-colors"
                      >
                        <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                          SKU {getSortIcon('sku')}
                        </span>
                      </button>
                    </th>
                    <th scope="col" className="px-6 py-3 text-center">
                      <ColumnFilter
                        columnName="category"
                        title="Categoría"
                        filterKey="category"
                        sortKey="category"
                        currentValue={filters.category}
                        options={getUniqueCategories().map(cat => ({ value: cat, label: `🏷️ ${cat}` }))}
                      />
                    </th>
                    <th scope="col" className="px-6 py-3 text-center w-48">
                      <ColumnFilter
                        columnName="stock"
                        title="Stock"
                        filterKey="stockLevel"
                        sortKey="current_stock"
                        currentValue={filters.stockLevel}
                        options={[
                          { value: 'agotado', label: '🔴 Agotado (Sin stock)' },
                          { value: 'critico', label: '🟠 Crítico (Bajo mínimo)' },
                          { value: 'bajo', label: '🟡 Bajo (Moderado)' },
                          { value: 'normal', label: '🟢 Normal (Óptimo)' }
                        ]}
                      />
                    </th>
                    <th scope="col" className="px-6 py-3 text-center">
                      <ColumnFilter
                        columnName="unit_price"
                        title="Precio"
                        filterKey="priceRange"
                        sortKey="unit_price"
                        currentValue={filters.priceRange}
                        options={[
                          { value: 'bajo', label: '💰 Bajo (< $100)' },
                          { value: 'medio', label: '💰💰 Medio ($100-$500)' },
                          { value: 'alto', label: '💰💰💰 Alto (> $500)' }
                        ]}
                      />
                    </th>
                    <th scope="col" className="px-6 py-3 text-center">
                      <ColumnFilter
                        columnName="location"
                        title="Ubicación"
                        filterKey="location"
                        sortKey="location"
                        currentValue={filters.location}
                        options={getUniqueLocations().map(loc => ({ value: loc, label: `📍 ${loc}` }))}
                      />
                    </th>
                    <th scope="col" className="px-6 py-3 text-center hidden">
                      <div className="flex items-center justify-center">
                        <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">Acciones</span>
                      </div>
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  <AnimatePresence>
                    {productsToShow
                      .filter(product => product && typeof product === 'object' && product.id) // Validación extra
                      .map((product) => {
                      const stockQuantity = product.current_stock || product.stock || product.quantity || 0;
                      const minStock = product.min_stock || product.minimum_stock || 5;
                      const maxStock = product.max_stock || product.maximum_stock || 50;
                      
                      return (
                        <motion.tr
                          key={product.id}
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, x: -20 }}
                          transition={{ duration: 0.3 }}
                          className={`cursor-pointer transition-all duration-200 ${getRowColorClass(stockQuantity, minStock, maxStock)}`}
                          onClick={() => navigate(`/inventory/product/${product.id}?page=${page}`)}
                          title="Clic para ver ficha técnica"
                        >
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <Hash className="w-4 h-4 text-gray-500 mr-2" />
                            <div className="text-sm font-medium text-gray-900">{product.code || 'N/A'}</div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <Tag className="w-4 h-4 text-gray-500 mr-2" />
                            <div className="text-sm font-medium text-gray-900">{product.name || 'Sin nombre'}</div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">{product.sku || 'N/A'}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            <ShoppingCart className="w-3 h-3 mr-1" />
                            {product.category || 'Sin categoría'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="w-44">
                            <StockProgressBar
                              current={stockQuantity}
                              min={minStock}
                              max={maxStock}
                              compact={true}
                            />
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900 font-medium">
                            <DollarSign className="w-4 h-4 inline mr-1" />
                            ${(parseFloat(product.unit_price) || 0).toFixed(2)}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-500">
                            <Warehouse className="w-4 h-4 inline mr-1" />
                            {product.location || 'No especificada'}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium hidden">
                          <div className="flex items-center space-x-3">
                            <motion.button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleEditProduct(product);
                              }}
                              className="text-blue-600 hover:text-blue-900 flex items-center gap-1"
                              whileHover={{ scale: 1.1 }}
                              whileTap={{ scale: 0.9 }}
                            >
                              <Edit className="w-4 h-4" />
                              Editar
                            </motion.button>
                            <motion.button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteProduct(product.id);
                              }}
                              className="text-red-600 hover:text-red-900 flex items-center gap-1"
                              whileHover={{ scale: 1.1 }}
                              whileTap={{ scale: 0.9 }}
                            >
                              <Trash2 className="w-4 h-4" />
                              Borrar
                            </motion.button>
                          </div>
                        </td>
                      </motion.tr>
                    )})}
                  </AnimatePresence>
                </tbody>
                </table>
              </div>
              
              {/* Controles de paginación */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between mt-6 px-4">
                  <div className="flex items-center text-sm text-gray-700">
                    <span>
                      Mostrando <span className="font-medium">{startIndex + 1}</span> a{' '}
                      <span className="font-medium">{Math.min(endIndex, totalProducts)}</span> de{' '}
                      <span className="font-medium">{totalProducts}</span> productos
                    </span>
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    <motion.button
                      onClick={() => handlePageChange(Math.max(page - 1, 1))}
                      disabled={page === 1}
                      className={`flex items-center px-4 py-2 text-sm font-medium rounded-lg transition-all duration-200 ${
                        page === 1
                          ? 'text-gray-400 cursor-not-allowed bg-gray-100'
                          : 'text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 hover:text-gray-900'
                      }`}
                      whileHover={page !== 1 ? { scale: 1.05 } : {}}
                      whileTap={page !== 1 ? { scale: 0.95 } : {}}
                    >
                      <ChevronLeft className="w-4 h-4 mr-1" />
                      Anterior
                    </motion.button>
                    
                    <div className="flex items-center space-x-1">
                      {Array.from({ length: totalPages }, (_, i) => i + 1)
                        .filter(p => {
                          const distance = Math.abs(p - page);
                          return distance === 0 || distance === 1 || p === 1 || p === totalPages;
                        })
                        .map((p, index, array) => {
                          const prevPage = array[index - 1];
                          const showEllipsis = prevPage && p - prevPage > 1;
                          
                          return (
                            <React.Fragment key={p}>
                              {showEllipsis && (
                                <span className="px-2 py-1 text-gray-500">...</span>
                              )}
                              <motion.button
                                onClick={() => handlePageChange(p)}
                                className={`px-3 py-2 text-sm font-medium rounded-lg transition-all duration-200 ${
                                  p === page
                                    ? 'bg-blue-600 text-white shadow-md'
                                    : 'text-gray-700 bg-white border border-gray-300 hover:bg-gray-50'
                                }`}
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                              >
                                {p}
                              </motion.button>
                            </React.Fragment>
                          );
                        })}
                    </div>
                    
                    <motion.button
                      onClick={() => handlePageChange(Math.min(page + 1, totalPages))}
                      disabled={page === totalPages}
                      className={`flex items-center px-4 py-2 text-sm font-medium rounded-lg transition-all duration-200 ${
                        page === totalPages
                          ? 'text-gray-400 cursor-not-allowed bg-gray-100'
                          : 'text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 hover:text-gray-900'
                      }`}
                      whileHover={page !== totalPages ? { scale: 1.05 } : {}}
                      whileTap={page !== totalPages ? { scale: 0.95 } : {}}
                    >
                      Siguiente
                      <ChevronRight className="w-4 h-4 ml-1" />
                    </motion.button>
                  </div>
                </div>
              )}
            </div>
          )}
          </motion.div>
        </>
      )}
    </motion.div>
  );
};

export default InventoryPage;